import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, DollarSign, FileText,
  CalendarDays, BarChart3, LogOut, Menu, X, ChevronRight, Settings,
  BookOpen, ClipboardCheck, Scale, CalendarRange, Umbrella, HandCoins
} from "lucide-react";

const navItems = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/employees", label: "Employees", icon: Users },
    { to: "/payroll", label: "Payroll", icon: DollarSign },
    { to: "/payslips", label: "Payslips", icon: FileText },
    { to: "/advances", label: "Advances", icon: HandCoins },
    { to: "/leave", label: "Leave", icon: CalendarDays },
    { to: "/leave-balances", label: "Leave Balances", icon: Umbrella },
    { to: "/holidays", label: "Holidays", icon: Scale },
    { to: "/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/training", label: "Training", icon: BookOpen },
    { to: "/performance", label: "Performance", icon: ClipboardCheck },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/settings", label: "Settings", icon: Settings },
  ],
  hr_manager: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/employees", label: "Employees", icon: Users },
    { to: "/payroll", label: "Payroll", icon: DollarSign },
    { to: "/payslips", label: "Payslips", icon: FileText },
    { to: "/advances", label: "Advances", icon: HandCoins },
    { to: "/leave", label: "Leave", icon: CalendarDays },
    { to: "/leave-balances", label: "Leave Balances", icon: Umbrella },
    { to: "/holidays", label: "Holidays", icon: Scale },
    { to: "/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/training", label: "Training", icon: BookOpen },
    { to: "/performance", label: "Performance", icon: ClipboardCheck },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/settings", label: "Settings", icon: Settings },
  ],
  employee: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/payslips", label: "My Payslips", icon: FileText },
    { to: "/leave", label: "My Leave", icon: CalendarDays },
    { to: "/training", label: "Training", icon: BookOpen },
    { to: "/performance", label: "My Reviews", icon: ClipboardCheck },
    { to: "/settings", label: "Settings", icon: Settings },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const role = user?.role || "employee";
  const items = navItems[role] || navItems.employee;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const pathSegments = location.pathname.split("/").filter(Boolean);

  return (
    <div className="flex h-screen overflow-hidden bg-white" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static lg:z-auto flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <span className="text-xl font-medium tracking-tight text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            PayrollKE
          </span>
          <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#002FA7] text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="h-4 w-4" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-sm transition-colors"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 flex items-center px-6 border-b border-slate-200 bg-white shrink-0">
          <button
            className="lg:hidden mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5 text-slate-700" />
          </button>

          <div className="flex items-center text-sm text-slate-500">
            {pathSegments.map((seg, i) => (
              <span key={i} className="flex items-center">
                {i > 0 && <ChevronRight className="h-3 w-3 mx-1" />}
                <span className={i === pathSegments.length - 1 ? "text-slate-900 font-medium capitalize" : "capitalize"}>
                  {seg.replace(/-/g, " ")}
                </span>
              </span>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 hidden sm:block">
              {role.replace("_", " ")}
            </span>
            <div className="h-8 w-8 rounded-sm bg-[#002FA7] flex items-center justify-center text-white text-xs font-medium">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
